// COACHING PERSONALITY — every English sentence this surface renders.
//
// WHY A MODULE AND NOT JSX LITERALS
// ---------------------------------
// This app has no key based i18n. The RENDERED ENGLISH SENTENCE IS THE KEY:
// TranslationContext walks the DOM, takes each text node's content, and looks
// it up in the flat `static` namespace that registerStaticTranslations built
// out of the bundles (contexts/TranslationContext.js:244-262). A sentence that
// differs from the registered one by a single comma is not found, and stays
// English in sixteen languages.
//
// Holding the sentences here, as plain string constants, does three things JSX
// literals cannot:
//   1. it makes the exactness testable — one test compares every value below
//      against src/translations/pages/profile/coaching-personality.json,
//   2. it removes the risk of JSX collapsing a source line break inside a long
//      sentence into something that no longer matches character for character,
//   3. it puts the "one sentence, one text node" rule in one place: each
//      constant is rendered as {S.x} and nothing else, so no sentence can ever
//      be assembled out of two elements.
//
// The separators the prototype writes between sentences (the middle dot, the
// pipe) are MARKUP, not text: they live in their own elements so the scanner
// never sees "sentence A · sentence B" as one node it cannot resolve.
//
// PROVENANCE of every value: the registered bundle
// src/translations/pages/profile/coaching-personality.json (feature branch
// feature/coaching-personality-translations), whose own source is
// TP_COACH_CHARACTER_KATALOG_V1.md and TP_COACH_CHARACTER_TRANSLATIONS(_2).md.
// The `context` column of each bundle row carries the catalogue id quoted here.
//
// Values marked EN-ONLY are the two sentences this work package adds (the
// private development comparison the brief decided on, WP2). They follow the
// repo convention for new copy: the English source is registered now and the
// translation pipeline fills the locale columns later.

const S = {
  // ── Section chrome ────────────────────────────────────────────────────────
  // id: ui.section.title
  sectionTitle: "Coaching Personality",
  // id: ui.tag
  tagSelfAssessment: "Self-assessment",
  // id: proto.ui.tag.bigfive
  tagBigFive: "Big Five based",
  // id: proto.ui.badge.private
  badgePrivate: "Only visible to you",

  // ── Empty state ───────────────────────────────────────────────────────────
  // id: ui.empty.hint
  emptyHint: "Add your coaching personality",
  // id: proto.ui.empty.subline
  emptySubline:
    "A portrait, not a test. About 10 minutes. Only visible to you until you release it.",
  // Already registered platform wide (pages/meetings/meetings.json).
  start: "Start",

  // ── Draft in progress ─────────────────────────────────────────────────────
  // id: proto.ui.resume.title
  resumeTitle: "Questionnaire in progress",
  // id: ui.progress — a TEMPLATE key. The whole sentence including the number
  // must be ONE text node, or the placeholder matcher never sees it.
  progress: (answered) =>
    `${answered} of 50 answered. Your draft is not visible anywhere.`,
  // id: ui.resume
  resume: "Continue where you left off",
  // id: ui.discard
  discard: "Discard",

  // ── Intro ─────────────────────────────────────────────────────────────────
  // id: intro.text — ONE string in the catalogue and ONE string in every
  // delivered locale. The prototype splits it across a heading and a paragraph;
  // rendered that way the scanner would look for two sentences that are not
  // registered and find neither. It renders as a single node here.
  introText:
    "There are no right or wrong answers. This is a portrait, not a test. Answer the way you actually work, not the way it sounds good. About 10 minutes, you can pause any time.",
  // id: intro.privacy
  introPrivacy:
    "Only you see your result first: stored, visible only after you release it, deletable any time.",
  // id: proto.ui.intro.count
  introCount: "50 statements",
  // id: proto.ui.notnow
  notNow: "Not now",

  // ── Questionnaire ─────────────────────────────────────────────────────────
  // id: ui.pause
  pause: "Pause",
  // Registered platform wide (pages/auth/auth.json and twelve more).
  back: "Back",
  next: "Next",
  // id: proto.ui.finish
  finish: "Finish",

  // ── Inner evaluation ──────────────────────────────────────────────────────
  // id: proto.ui.result.intro
  resultIntro:
    "Your portrait across five axes, from your 50 answers. Neutral by design: no scores, no ranking, no overall grade.",
  // id: eval.intro
  evalIntro:
    "How to read your result: each axis is a spectrum between two equally valid ways of working. The marker shows where your answers place you. No position is better than another. The same trait that is a strength in one setup can create friction in another. That is exactly what this portrait is for.",
  // id: proto.ui.level.eyebrow
  levelEyebrow: "In everyday work",
  // id: ui.date — TEMPLATE key, one node.
  asOf: (date) => `As of ${date}`,
  // id: ui.lock — TEMPLATE key, one node.
  lockUntil: (date) => `Update possible from ${date}`,
  // id: ui.due
  due: "You can update your profile now",
  // id: proto.ui.release.title
  releaseTitle: "Release your card",
  // id: proto.ui.release.explainer
  releaseExplainer:
    "Off means only you see the result. You can change this at any time.",
  // id: ui.toggle.label — the toggle's own label, per the build brief ("the
  // toggle label comes from the fixed strings").
  toggleLabel: "Visible to clubs I apply to",
  // id: ui.toggle.explainer
  toggleExplainer:
    "Clubs that have received a pitch or an application from you see this on your profile. You can switch it off any time.",
  // id: ui.retake
  retake: "Retake questionnaire",
  // Registered platform wide (components/contacts/delete-confirmation-modal.json).
  delete: "Delete",
  cancel: "Cancel",
  close: "Close",

  // ── Footer, assembled from FIVE registered sentences in five elements ─────
  // id: proto.ui.footer.catalog — the literal reads "Catalog v1" for catalogue
  // v1, which is what is registered. The version comes from the server; a later
  // catalogue renders its own number rather than a wrong one.
  catalogVersion: (version) => `Catalog ${version}`,
  // id: proto.ui.footer.version
  footerVersion: "Your result keeps its catalog version.",
  // id: proto.ui.footer.language
  footerLanguage:
    "Released, the card renders for each viewer in their own language.",

  // ── Delete confirmation ───────────────────────────────────────────────────
  // id: proto.ui.delete.confirm.title
  deleteTitle: "Delete coaching personality?",
  // id: proto.ui.delete.confirm.body — the lock sentence that follows it in the
  // prototype is a SECOND registered sentence and gets its own element.
  deleteBody: "Your result is removed immediately and everywhere.",

  // ── Outer card (club side) ────────────────────────────────────────────────
  // id: proto.ui.card.zoom.aria — rendered as aria-label, which the scanner
  // translates like a text node (TranslationContext.js:22).
  zoomAria: "Open detailed view",
  // id: proto.ui.card.zoom.title — rendered as title.
  zoomTitle: "Detailed view",
  // id: proto.ui.zoom.footer
  zoomFooter:
    "Self-assessment by the coach. No scores, no ranking, no overall grade.",

  // ── Private development comparison (WP2 decision) ─────────────────────────
  // The only two sentences this work package adds. They are registered in
  // pages/profile/coaching-personality-ui.json WITH all sixteen locale columns,
  // because the repo does not tolerate an English-only row: Croatian never
  // calls the runtime translation service (TranslationContext.js:20), so a gap
  // there stays English on a user's screen for good, and
  // staticBundleCompleteness.test.js fails the build over it. The values are
  // authored by this build and flagged for Marin's review, not smuggled in.
  developmentTitle: "Your development",
  developmentExplainer: "Only you see this. Your last two results, side by side.",
};

// WHAT IS DELIBERATELY NOT HERE: the five axis subtitles the prototype prints
// under each axis name ("Emotional stability", "Address and presence" ...).
// They are prototype chrome, not catalogue text: Konzept 4a lists what the
// inner evaluation carries and they are not in that list, the catalogue and all
// sixteen delivered locales do not contain them, and Konzept 3.3 wants the Big
// Five claim made honestly ONCE ("Self-assessment, Big Five based", which the
// two pills and the footer do) rather than as a construct name printed beside
// every trainer-language axis label. Shipping them would have meant either a
// permanently English line in Croatian or five psychological terms translated
// into fifteen languages by this build. Restoring them is one line here plus
// one bundle row per sentence, if Marin's next batch delivers axis.N.sub.

export default S;
